import { devLog } from "Util";
import { TotalMapType } from "apis/library/library.api";
import { DEV_MODE } from "config";
import { SortType } from "types/common/CommonType";
import { create } from "zustand";

export interface ProjectDataType {
  projectVersion: string;
  lastProjectVersion: string;
  projectId: string;
  projectName: string;
  description: string;
  rows: number;
  createdDate: string;
  lastModifiedDate: string;
  libraryId?: string;
  libraryName?: string;
  libraryVersion: string;
  libraryDetailId?: string;
  sourceLibraryName?: string;
  inheritanceTagNameList?: string[];
}

export interface DatasetInfo {
  currentLibraryDetailInfo: {
    libraryColumns: number;
    libraryRows: number;
    libraryVersion: number;
    stepId: string | number;
  };
  newestLibraryDetailInfo: {
    libraryColumns: number;
    libraryRows: number;
    libraryVersion: number;
    stepId: string | number;
  };
}

interface ProjectDataStore {
  projectList: any[];
  projectListSort: SortType;
  projectTotalMap: TotalMapType;
  connectDatasetMode: boolean;
  changeDatasetMode: boolean;
  preprocessConnectMode: boolean;
  publishAnswerMode: boolean;
  projectData: ProjectDataType;
  projectSearch: string;
  isProjectInfoDashboard: boolean;
  datasetInfoList: DatasetInfo[];
  refreshAbleList: string[];
  createProjectName: string;
  setProjectList: (list: any[], isInit: boolean) => void;
  setProjectListSort: (column: string, order: "asc" | "desc") => void;
  setProjectTotalMap: (totalMap: TotalMapType) => void;
  setConnectDatasetMode: (value: boolean) => void;
  setChangeDatasetMode: (value: boolean) => void;
  setPreprocessConnectMode: (value: boolean) => void;
  setPublishAnswerMode: (value: boolean) => void;
  setProjectData: (newData: Partial<ProjectDataType>) => void;
  setProjectSearch: (value: string) => void;
  setIsProjectInfoDashboard: (view: boolean) => void;
  setDatasetInfoList: (datasetInfoList: DatasetInfo[]) => void;
  setRefreshAbleList: (idList: string[]) => void;
  setCreateProjectName: (name: string) => void;
  resetProjectData: () => void;
}

const useProjectDataStore = create<ProjectDataStore>((set) => ({
  projectList: [],
  projectTotalMap: { totalRows: 0, totalPages: 0, allRows: 0 },
  projectListSort: { column: "lastModifiedDate", order: "desc" },
  connectDatasetMode: false,
  changeDatasetMode: false,
  preprocessConnectMode: false,
  publishAnswerMode: false,
  projectData: {
    rows: 0,
    projectId: "",
    projectName: "",
    description: "",
    createdDate: "",
    projectVersion: "",
    lastProjectVersion: "",
    lastModifiedDate: "",
    libraryVersion: "",
    sourceLibraryName: "",
    libraryDetailId: "",
  },
  projectSearch: "",
  isProjectInfoDashboard: false,
  datasetInfoList: [],
  refreshAbleList: [],
  createProjectName: "",
  setProjectList: (list: any[], isInit: boolean) =>
    set((state) => ({
      projectList: isInit ? list : [...state.projectList, ...list],
    })),
  setProjectListSort: (column: string, order: "asc" | "desc") => {
    set({ projectListSort: { column, order } });
  },
  setProjectTotalMap: (totalMap: TotalMapType) =>
    set({ projectTotalMap: totalMap }),
  setConnectDatasetMode: (value: boolean) => set({ connectDatasetMode: value }),
  setChangeDatasetMode: (value: boolean) => set({ changeDatasetMode: value }),
  setPreprocessConnectMode: (value: boolean) =>
    set({ preprocessConnectMode: value }),
  setPublishAnswerMode: (value: boolean) => set({ publishAnswerMode: value }),
  setProjectData: (newData: Partial<ProjectDataType>) =>
    set((state) => ({
      projectData: { ...state.projectData, ...newData },
    })),
  setProjectSearch: (value: string) => set({ projectSearch: value }),
  setIsProjectInfoDashboard: (view: boolean) =>
    set({
      isProjectInfoDashboard: view,
    }),
  setDatasetInfoList: (datasetInfoList: DatasetInfo[]) =>
    set({ datasetInfoList: datasetInfoList }),
  setRefreshAbleList: (idList: string[]) => set({ refreshAbleList: idList }),
  setCreateProjectName: (name: string) => set({ createProjectName: name }),
  resetProjectData: () => {
    set({
      projectList: [],
      projectTotalMap: { totalRows: 0, totalPages: 0, allRows: 0 },
      projectListSort: { column: "lastModifiedDate", order: "desc" },
      connectDatasetMode: false,
      changeDatasetMode: false,
      preprocessConnectMode: false,
      publishAnswerMode: false,
      projectData: {
        projectVersion: "",
        lastProjectVersion: "",
        sourceLibraryName: "",
        libraryDetailId: "",
        libraryVersion: "",
        projectId: "",
        projectName: "",
        description: "",
        rows: 0,
        createdDate: "",
        lastModifiedDate: "",
      },
      projectSearch: "",
      isProjectInfoDashboard: false,
      datasetInfoList: [],
      refreshAbleList: [],
      createProjectName: "",
    });
  },
}));

// 값의 변경을 감지하여 콘솔에 출력하는 코드
useProjectDataStore.subscribe((state) => {});

export default useProjectDataStore;
